import React, { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Clock, BarChart3, Target, Activity, ShieldCheck, Sparkles, Gauge, Lightbulb, Lock } from "lucide-react";
import { usePrematch, useMarketHubMap } from "@/hooks/useAtlas";
import { resolvePrematchFixture } from "@/api/marketHub";
import { useSession } from "@/context/SessionContext";
import { MarketHubSections, AdvancedLock } from "@/components/cards/MarketHub";
import { useLaunch } from "@/context/LaunchContext";
import { LoadingRows, ErrorState, EmptyState } from "@/components/common/States";
import { MarketBadge, CompetitionLabel, RiskChip } from "@/components/badges/Badges";
import { TeamLogo, resolveTeamVisual } from "@/components/common/TeamLogo";
import { FollowButton, AddToSlipButton } from "@/components/cards/Actions";
import { IntelligenceCard } from "@/components/cards/Cards";
import { SummaryTile, CompareRow, PctBar, FormChips, compactCells } from "@/components/cards/PrematchMetrics";
import { presentOdd } from "@/presentation/oddsPresentation";
import { kickoffLabel } from "@/lib/format";
import { FootyStatsMetrics, PrematchFixture } from "@/types/api";
import { useI18n } from "@/i18n";

type Lang = "tr" | "en";
const fin = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const pct = (v: number) => `%${Math.round(v)}`;
const dec = (v: number) => v.toFixed(2);
const tt = (lang: Lang, tr: string, en: string) => (lang === "tr" ? tr : en);

// Customer-facing explanation — intro + ONLY the supporting metrics that exist.
// No causal claims, no success/profit promises; evidence framing only.
function explain(code: string, fs: FootyStatsMetrics, lang: Lang): { intro: string; points: { label: string; value: string }[] } {
  const c = (code || "").toUpperCase();
  const points: { label: string; value: string }[] = [];
  const add = (ok: boolean, label: string, value: string) => { if (ok) points.push({ label, value }); };
  let intro: string;
  if (c.includes("UNDER_3_5") || c.includes("UNDER35") || c.includes("U3_5")) {
    intro = tt(lang, "ATLAS bu seçimde, düşük toplam gol profiline işaret eden maç verilerini birlikte değerlendiriyor.", "ATLAS weighs match data that together point to a lower total-goals profile for this pick.");
    add(fin(fs.under_35_pct), tt(lang, "3.5 Alt potansiyeli", "Under 3.5 potential"), fin(fs.under_35_pct) ? pct(fs.under_35_pct!) : "");
    add(fin(fs.total_xg), tt(lang, "Toplam xG", "Total xG"), fin(fs.total_xg) ? dec(fs.total_xg!) : "");
    add(fin(fs.avg_goals_potential), tt(lang, "Gol ortalaması", "Avg goals"), fin(fs.avg_goals_potential) ? dec(fs.avg_goals_potential!) : "");
    add(fin(fs.btts_pct), "BTTS", fin(fs.btts_pct) ? pct(fs.btts_pct!) : "");
  } else if (c.includes("DOUBLE_CHANCE") || c.includes("DC_X2") || c.endsWith("_X2") || c.includes("X2")) {
    intro = tt(lang, "ATLAS bu seçimde taraf güç dengesini ve beklenen gol üretimini birlikte değerlendiriyor.", "ATLAS weighs the balance of team strength and expected goal output for this pick.");
    add(fin(fs.home_ppg), tt(lang, "Ev PPG", "Home PPG"), fin(fs.home_ppg) ? dec(fs.home_ppg!) : "");
    add(fin(fs.away_ppg), tt(lang, "Deplasman PPG", "Away PPG"), fin(fs.away_ppg) ? dec(fs.away_ppg!) : "");
    add(fin(fs.home_xg), tt(lang, "Ev xG", "Home xG"), fin(fs.home_xg) ? dec(fs.home_xg!) : "");
    add(fin(fs.away_xg), tt(lang, "Deplasman xG", "Away xG"), fin(fs.away_xg) ? dec(fs.away_xg!) : "");
    add(fin(fs.total_xg), tt(lang, "Toplam xG", "Total xG"), fin(fs.total_xg) ? dec(fs.total_xg!) : "");
  } else {
    intro = tt(lang, "ATLAS bu seçimi, aşağıdaki maç verilerini birlikte değerlendirerek üretiyor.", "ATLAS forms this pick by weighing the match data below together.");
    add(fin(fs.total_xg), tt(lang, "Toplam xG", "Total xG"), fin(fs.total_xg) ? dec(fs.total_xg!) : "");
    add(fin(fs.over_25_pct), tt(lang, "2.5 Üst", "Over 2.5"), fin(fs.over_25_pct) ? pct(fs.over_25_pct!) : "");
    add(fin(fs.btts_pct), "BTTS", fin(fs.btts_pct) ? pct(fs.btts_pct!) : "");
    add(fin(fs.avg_goals_potential), tt(lang, "Gol ortalaması", "Avg goals"), fin(fs.avg_goals_potential) ? dec(fs.avg_goals_potential!) : "");
  }
  return { intro, points };
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.03] p-3 text-center" data-testid="fs-dynamic-tile">
      <p className="num text-lg font-extrabold text-foreground">{value}</p>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
}

export default function PrematchDetail() {
  const { id = "" } = useParams();
  const { lang } = useI18n();
  const { isAuthenticated } = useSession();
  // SAME canonical authenticated Prematch V2 dataset/normalizer as /app/prematch.
  const prematch = usePrematch(120, isAuthenticated);
  const { lookup: hubLookup, view: mhView, isLoading: mhLoading } = useMarketHubMap();
  const { hasPlan } = useLaunch();
  const canAdvanced = hasPlan("ELITE");
  // FREE: no actionable CURRENT prematch recommendation (primary/odds/confidence/
  // edge/risk/metrics/alternatives/watching/reasoning). Detail is discovery-only.
  const canPick = hasPlan("PRO");
  // CANONICAL: Market Hub prematch OWNS fixture existence (exactly like the
  // list). Legacy prematch may only enrich by fixture_id; it is NOT required.
  const f: PrematchFixture | undefined = useMemo(
    () => resolvePrematchFixture(id, prematch.data?.rows || [], mhView?.prematch) || undefined,
    [prematch.data, mhView, id],
  );

  const back = (
    <Link to="/app/prematch" className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground transition hover:text-atlas-300">
      <ArrowLeft className="h-4 w-4" /> {lang === "tr" ? "Maç Öncesi" : "Prematch"}
    </Link>
  );

  if (!f) {
    if (prematch.isLoading || (isAuthenticated && mhLoading && !mhView)) {
      return <div data-testid="prematch-detail-page">{back}<LoadingRows count={4} /></div>;
    }
    if (prematch.isError && !(isAuthenticated && mhView)) {
      return <div data-testid="prematch-detail-page">{back}<ErrorState onRetry={() => { prematch.refetch(); }} /></div>;
    }
    return (
      <div data-testid="prematch-detail-page">
        {back}
        <EmptyState
          title={lang === "tr" ? "Maç bulunamadı" : "Match not found"}
          hint={lang === "tr" ? "Bu maç artık aktif maç öncesi seçimler arasında değil." : "This match is no longer among the active prematch selections."}
        />
      </div>
    );
  }

  const best = f.best || f.markets?.[0] || null;
  const hub = hubLookup(f);
  // MarketHub.primary (FootyStats-aligned, canonical) OVERRIDES the legacy
  // prematch selection for the visible ATLAS pick. Legacy is fallback only.
  const hubPrimary = hub?.primary && hub.primary.is_recommendation !== false ? hub.primary : null;
  const pick = hubPrimary
    ? { market_code: hubPrimary.market_code, market_name: hubPrimary.market_name ?? null, model_probability: hubPrimary.probability ?? null, edge: hubPrimary.edge ?? null, reference_odd: hubPrimary.reference_odd ?? null }
    : best;
  const fs = f.footystats_metrics || best?.footystats_metrics || null;
  const odd = pick ? presentOdd({ value: pick.reference_odd, source: null }) : null;
  const homeLogo = resolveTeamVisual(f.home_team, "home", [f, best, fs]);
  const awayLogo = resolveTeamVisual(f.away_team, "away", [f, best, fs]);
  const leagueLogo = f.league_logo || fs?.league_logo || null;
  const label = `${f.home_team} — ${f.away_team}`;

  const summary = fs ? compactCells(fs, pick?.market_code || "", lang) : [];
  const compareRows = fs ? ([
    { label: "PPG", home: fs.home_ppg, away: fs.away_ppg },
    { label: "xG", home: fs.home_xg, away: fs.away_xg },
    { label: "xGA", home: fs.home_xga, away: fs.away_xga },
  ].filter((r) => fin(r.home) || fin(r.away))) : [];
  const goalBars = fs ? ([
    { label: tt(lang, "1.5 Üst", "Over 1.5"), value: fs.over_15_pct },
    { label: tt(lang, "2.5 Üst", "Over 2.5"), value: fs.over_25_pct },
    { label: tt(lang, "3.5 Üst", "Over 3.5"), value: fs.over_35_pct },
    { label: tt(lang, "3.5 Alt", "Under 3.5"), value: fs.under_35_pct },
    { label: "BTTS", value: fs.btts_pct },
  ].filter((b) => fin(b.value)) as { label: string; value: number }[]) : [];
  const dynamics = fs ? ([
    fin(fs.corners_avg) ? { label: tt(lang, "Korner Ort.", "Corners Avg"), value: dec(fs.corners_avg!) } : null,
    fin(fs.cards_avg) ? { label: tt(lang, "Kart Ort.", "Cards Avg"), value: dec(fs.cards_avg!) } : null,
    fin(fs.avg_goals_potential) ? { label: tt(lang, "Gol Ort.", "Avg Goals"), value: dec(fs.avg_goals_potential!) } : null,
  ].filter(Boolean) as { label: string; value: string }[]) : [];
  const hasForm = !!(fs && ((typeof fs.home_form === "string" && fs.home_form.trim()) || (typeof fs.away_form === "string" && fs.away_form.trim())));
  const h2h = fs?.h2h_present === true;
  const trends = fs?.trends_present === true;
  const reasoning = fs && pick ? explain(pick.market_code, fs, lang) : null;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} data-testid="prematch-detail-page">
      {back}
      <div className="mx-auto max-w-3xl space-y-5">
        {/* PREMIUM HEADER */}
        <div className="panel relative overflow-hidden p-6" data-testid="prematch-hero">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-atlas/10 blur-3xl" />
          <div className="flex items-center justify-between gap-3">
            <CompetitionLabel country={f.country} league={f.league} leagueLogo={leagueLogo} />
            <span className="inline-flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] font-semibold text-muted-foreground" data-testid="prematch-kickoff">
              <Clock className="h-3 w-3" /> {kickoffLabel(f.fixture_date, lang)}
            </span>
          </div>
          <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-end">
              <TeamLogo src={homeLogo} name={f.home_team} size={40} rounded="rounded-lg" />
              <p className="text-center text-base font-bold text-foreground sm:text-right sm:text-xl">{f.home_team}</p>
            </div>
            <span className="text-sm font-semibold text-muted-foreground">vs</span>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-start">
              <TeamLogo src={awayLogo} name={f.away_team} size={40} rounded="rounded-lg" />
              <p className="text-center text-base font-bold text-foreground sm:text-left sm:text-xl">{f.away_team}</p>
            </div>
          </div>
          {canPick && pick && (
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-atlas/20 bg-atlas/[0.05] p-3">
              <div>
                <p className="eyebrow text-atlas-300">{lang === "tr" ? "ATLAS seçimi" : "ATLAS pick"}</p>
                <div className="mt-1.5"><MarketBadge code={pick.market_code} backendLabel={pick.market_name} /></div>
              </div>
              <div className="flex items-center gap-3">
                {odd?.show && <span className="num text-xs font-semibold text-atlas-300">{lang === "tr" ? "Referans Oran" : "Ref Odds"} {odd.value}</span>}
                <RiskChip code={pick.market_code} />
              </div>
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {canPick && pick && <AddToSlipButton size="sm" selection={{ fixtureId: f.fixture_id, home: f.home_team, away: f.away_team, country: f.country, league: f.league, marketCode: pick.market_code, marketName: pick.market_name, confidence: pick.model_probability, edge: pick.edge, referenceOdd: pick.reference_odd, kickoff: f.fixture_date }} />}
            <FollowButton fixtureId={f.fixture_id} label={label} league={f.league} size="sm" />
          </div>
        </div>

        {/* FREE: discovery-only — ATLAS analysis is fully locked (no recommendation metadata) */}
        {!canPick && (
          <div className="panel relative overflow-hidden p-6 text-center" data-testid="prematch-detail-locked">
            <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-gold/10 blur-3xl" />
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border border-gold/30 bg-gold/10 text-gold-300"><Lock className="h-6 w-6" /></span>
            <h3 className="mt-4 text-lg font-extrabold tracking-tight text-foreground">{lang === "tr" ? "ATLAS analizi hazır" : "ATLAS analysis ready"}</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{lang === "tr" ? "ATLAS bu maçı değerlendirdi. Ana seçim, oranlar, güven, alternatifler ve tam istatistik analizi PRO ile açılır." : "ATLAS has evaluated this match. The primary pick, odds, confidence, alternatives and full statistical analysis unlock with PRO."}</p>
            <Link to="/app/membership" data-testid="prematch-detail-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-5 py-2.5 text-sm font-bold text-atlas-ink transition hover:shadow-glow"><Sparkles className="h-4 w-4" /> {lang === "tr" ? "PRO ile Aç" : "Unlock with PRO"}</Link>
          </div>
        )}

        {canPick && (<>
        {hub && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "ATLAS Seçimleri" : "ATLAS Selections"}</span>}>
            <MarketHubSections fixture={hub} variant="detail" />
          </IntelligenceCard>
        )}

        {!fs && (
          <EmptyState title={lang === "tr" ? "Ek istatistik yok" : "No extra statistics"} hint={lang === "tr" ? "Bu seçim için ek maç öncesi istatistik verisi henüz mevcut değil." : "No additional prematch statistics are available for this selection yet."} />
        )}

        {/* ATLAS SELECTION EXPLANATION */}
        {reasoning && reasoning.points.length > 0 && canAdvanced && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Lightbulb className="h-4 w-4 text-gold-400" /> {lang === "tr" ? "ATLAS Seçimi Neden?" : "Why ATLAS Picked This"}</span>}>
            <p className="text-sm text-foreground/80">{reasoning.intro}</p>
            <div className="mt-3 grid gap-1.5 sm:grid-cols-2">
              {reasoning.points.map((p) => (
                <div key={p.label} className="flex items-center justify-between gap-2 rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
                  <span className="text-xs text-muted-foreground">{p.label}</span>
                  <span className="num text-sm font-bold text-foreground/90">{p.value}</span>
                </div>
              ))}
            </div>
          </IntelligenceCard>
        )}

        {/* A) MAÇ ÖZETİ (basic — all plans) */}
        {summary.length > 0 && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Gauge className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Maç Özeti" : "Match Summary"}</span>}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {summary.map((c, i) => <SummaryTile key={c.label} label={c.label} value={c.value} accent={i === 0} />)}
            </div>
          </IntelligenceCard>
        )}

        {fs && !canAdvanced && <AdvancedLock />}

        {/* B) TAKIM KARŞILAŞTIRMASI */}
        {compareRows.length > 0 && canAdvanced && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Activity className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Takım Karşılaştırması" : "Team Comparison"}</span>}>
            <div className="mb-3 grid grid-cols-[2.6rem_1fr_2.6rem] items-center gap-2 text-[11px] font-semibold text-muted-foreground">
              <span className="truncate text-left">{f.home_team}</span><span className="text-center">{lang === "tr" ? "Metrik" : "Metric"}</span><span className="truncate text-right">{f.away_team}</span>
            </div>
            <div className="space-y-3">{compareRows.map((r) => <CompareRow key={r.label} label={r.label} home={r.home} away={r.away} />)}</div>
          </IntelligenceCard>
        )}

        {/* C) GOL PROFİLİ */}
        {goalBars.length > 0 && canAdvanced && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Target className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Gol Profili" : "Goal Profile"}</span>}>
            <div className="space-y-2.5">{goalBars.map((b) => <PctBar key={b.label} label={b.label} value={b.value} />)}</div>
          </IntelligenceCard>
        )}

        {/* D) MAÇ DİNAMİĞİ */}
        {dynamics.length > 0 && canAdvanced && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><BarChart3 className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Maç Dinamiği" : "Match Dynamics"}</span>}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">{dynamics.map((t2) => <Tile key={t2.label} label={t2.label} value={t2.value} />)}</div>
          </IntelligenceCard>
        )}

        {/* E / F) FORM + H2H / TREND */}
        {(hasForm || h2h || trends) && fs && canAdvanced && (
          <IntelligenceCard title={<span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Form & Eğilimler" : "Form & Trends"}</span>}>
            {hasForm && (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                {typeof fs.home_form === "string" && fs.home_form.trim() && <span className="flex items-center gap-2">{f.home_team} <FormChips form={fs.home_form} /></span>}
                {typeof fs.away_form === "string" && fs.away_form.trim() && <span className="flex items-center gap-2">{f.away_team} <FormChips form={fs.away_form} /></span>}
              </div>
            )}
            {(h2h || trends) && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                {h2h && <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[11px] font-semibold text-foreground/70"><Sparkles className="h-3 w-3 text-atlas-400" /> {lang === "tr" ? "H2H verisi mevcut" : "H2H data available"}</span>}
                {trends && <span className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-0.5 text-[11px] font-semibold text-foreground/70"><Sparkles className="h-3 w-3 text-atlas-400" /> {lang === "tr" ? "Trend verisi mevcut" : "Trend data available"}</span>}
              </div>
            )}
          </IntelligenceCard>
        )}

        {fs && <p className="text-center text-[10px] uppercase tracking-wide text-muted-foreground/60">{lang === "tr" ? "Veri: FootyStats" : "Data: FootyStats"}</p>}
        </>)}
      </div>
    </motion.div>
  );
}
